/* global moment, OpalRadio */

AppBase.module('Entities.Content', function (Module) {
  'use strict'

  // The name of this property doesn't matter too much since the server won't
  // return it and it'll be dropped from the model. This is just a convenient
  // one for debugging.
  var dirtyKey = 'dirtiedByLongform'

  var Model = Backbone.RelationalModel.extend({
    url: function () {
      var post = this.get('post')
      if (!post) return null

      var url = new URL(window.location.origin + _.result(post, 'url'))
      url.pathname += '/longform'

      return url.pathname
    },

    revertUrl: function () {
      var url = this.url()
      if (!url) return null

      var revertUrl = new URL(window.location.origin + url)
      revertUrl.pathname += '/revert'

      return revertUrl.pathname
    },

    defaults: {
      character_count: 0,
      displayedVersion: null,
      paragraph_count: 0,
      sentence_count: 0,
      title: '',
      versions_list_shown: false,
      word_count: 0,
    },

    relations: [
      {
        type: Backbone.HasMany,
        key: 'content_blocks',
        relatedModel: 'AppBase.Entities.Content.ContentBlock',
        collectionType: 'AppBase.Entities.Content.ContentBlocks',
        includeInJSON: true,
      },
      {
        type: Backbone.HasOne,
        key: 'post',
        keySource: 'post_id',
        relatedModel: 'OpalMoments.Models.Post',
        collectionType: 'OpalMoments.Collections.Posts',
        includeInJSON: Backbone.Model.prototype.idAttribute,
      },
      {
        type: Backbone.HasMany,
        key: 'versions',
        relatedModel: 'AppBase.Entities.Content.LongformVersion',
        collectionType: 'AppBase.Entities.Content.LongformVersions',
        includeInJSON: false,
      },
    ],

    initialize: function () {
      var displayedVersion

      if (!this.get('versions')) this.fetchLatestVersions()

      this.get('post').checkInIfTimedOut()

      if (this.get('post').checkedOutByMe())
        this.set('displayedVersion', 'checkout')
      else {
        displayedVersion = this.getMostRecentVersion()
        if (displayedVersion)
          this.set('displayedVersion', displayedVersion.get('version_number'))
      }

      this.handleContentBlocks()

      this.get('post').on(
        'change:check_out',
        function (post) {
          if (post.checkedOutByMe()) this.set('displayedVersion', 'checkout')

          this.lazilyFetchCheckOutUser()
        }.bind(this)
      )

      this.get('post').on(
        'checked_in',
        function () {
          this.fetchLatestVersions()
        }.bind(this)
      )

      this.on(
        'change:versions',
        function () {
          // Not sure if this is the correct behavior -- ideally the displayed
          // version wouldn't be affected as long as the relevant model is still
          // in the collection. A future enhancement, perhaps.
          this.showVersion(null)
        }.bind(this)
      )
    },

    /**
     * Fetch the user who currently has the content checked out.
     *
     * @access private
     * @memberof AppBase.Entities.Content.Longform
     */
    lazilyFetchCheckOutUser: function () {
      var checkout = this.get('post').get('check_out')
      // @note The user will be `undefined` in the case that a different user
      //   checks out the model, and the current user also attempts to check
      //   out the content -- the server response indicates the second checkout
      //   attempt has failed, and doesn't include a user. (This scenario
      //   occurs when push messaging isn't working, so the current user's
      //   browser doesn't realize the model has already been checked out.)
      //
      //   The best fix is to address this upstream -- the server response has
      //   a `200` status code, so Backbone creates a model from the response.
      //   The problem simply *manifests* here because of the missing `user`
      //   field.
      if (checkout && checkout.get('user')) checkout.get('user').fetch()
    },

    /**
     * Add logic for working with the content block association.
     *
     * @access private
     * @memberof AppBase.Entities.Content.Longform
     */
    handleContentBlocks: function () {
      var collection = this.get('content_blocks')

      // This steps around serialization problems. The content block collection
      // needs to know this model's `url` to generate its own URLs.
      //
      // Unfortunately, simply using the `longform` association causes infinite
      // recursion: when backbone-relational serializes a content block model
      // it also serializes relations, so it serializes longform which
      // serializes the content blocks which serializes...
      //
      // I tried to get around the problem by setting a `reverseRelation` flag
      // on the `content_blocks` relation, but it causes its own problems.
      //
      // Setting a string property on the collection gets around the recursion.
      collection.post = this
      collection.longform_url = this.url()

      collection.on('change', this.checkOutIfDirty.bind(this))
    },

    /**
     * Get the most recently-saved `Version` record. Note that this does *not*
     * include drafts.
     *
     * @returns {(AppBase.Entities.Content.Longform|null)} The most recent
     *   version.
     *
     * @memberof AppBase.Entities.Content.Longform
     */
    getMostRecentVersion: function () {
      return this.get('versions').first()
    },

    /**
     * Get the displayed version:.
     *
     * * `'checkout'` if the user has the content checked out
     * * a `Number` if a version is shown,
     * * `null` if no version is shown.
     *
     * @returns {(number|string|null)} The current version.
     *
     * @memberof AppBase.Entities.Content.Longform
     */
    getDisplayedVersionNumber: function () {
      return this.get('displayedVersion')
    },

    /**
     * Get the model for the displayed version:.
     *
     * * `this` if the content is checked out or unknown,
     * * a `LongformVersion` if a version is shown.
     *
     * @returns {(AppBase.Entities.Content.Longform|AppBase.Entities.Content.LongformVersion)}
     *   The displayed model.
     *
     * @memberof AppBase.Entities.Content.Longform
     */
    getDisplayedVersionModel: function () {
      var version = this.getDisplayedVersionNumber()
      if (!version || version === 'checkout') return this

      return this.get('versions').findWhere({ version_number: version })
    },

    getDisplayedContentBlocks: function () {
      var currentVersion = this.getDisplayedVersionNumber() === 'checkout'
      var model = this.getDisplayedVersionModel()
      if (currentVersion) {
        return model.get('content_blocks').map(function (cb) {
          return cb.attributes
        })
      }
      if (model && model.get('content')) {
        return model.get('content').content_blocks || []
      }
      return []
    },

    getPriorToDisplayedVersionModel: function () {
      var versionNumber = parseInt(this.getDisplayedVersionNumber(), 10)
      if (isNaN(versionNumber)) {
        return
      }
      var priorVersionNumber = versionNumber - 1
      return this.get('versions').findWhere({
        version_number: priorVersionNumber,
      })
    },

    fetchLatestVersions: function () {
      return this.fetch().then(
        function () {
          this.trigger('versions:updated')
        }.bind(this)
      )
    },

    /**
     * Determine whether the post model is dirty.
     *
     * @param {AppBase.Entities.Content.ContentBlock} model - A model instance.
     * @returns {boolean} Whether the model is dirty.
     *
     * @access private
     * @memberof AppBase.Entities.Content.Longform
     */
    checkIfDirty: function (model) {
      var cache

      var post = this.get('post')
      if (!post) return false
      if (!post.checkedOutByMe()) return false
      if (post.get(dirtyKey)) return true

      cache = model.collection.cache.get(model.id)
      return model.isDirty(cache)
    },

    /**
     * Mark the post model as dirty so the user can trigger a full save and
     * check the longform content back in.
     *
     * @param {AppBase.Entities.Content.ContentBlock} model - A model instance.
     *
     * @access private
     * @memberof AppBase.Entities.Content.Longform
     */
    checkOutIfDirty: function (model) {
      var post = this.get('post')
      var isDirty = this.checkIfDirty(model)

      if (isDirty) {
        post.set(dirtyKey, true)
        post.checkOut()

        this.trigger('dirtied')
      }
    },

    isEditable: function () {
      return (
        this.get('post').checkedOutByMe() &&
        this.getDisplayedVersionNumber() === 'checkout'
      )
    },

    /**
     * Serialize data for the React version list. Includes data that's marked
     * `includeInJSON:false`.
     *
     * @returns {Object} - The serialized data.
     *
     * @memberof AppBase.Entities.Content.Longform
     */
    toReactJSON: function () {
      var post = this.get('post')
      var checkout = post.get('check_out')
      var displayedVersion = this.getDisplayedVersionNumber()
      var editable =
        (post.checkedOutByMe() || !post.checkedOut()) &&
        displayedVersion === 'checkout'

      var data = {
        editable: editable,
      }

      data.versions = this.get('versions').map(function (version, index) {
        var user = version.get('user')

        // @note Users can't restore versions if someone else has the content
        //   checked out.
        var restorable =
          (post.checkedOutByMe() || !post.checkedOut()) &&
          version.get('version_number') > 1

        return {
          current: !checkout && index === 0,
          restorable: restorable,
          selected: version.get('version_number') === displayedVersion,
          timestamp: moment(version.get('created_at')),
          userId: user ? user.get('id').toString() : null,
          username: user ? user.get('full_name') : '',
          versionId: version.id,
          versionNumber: version.get('version_number'),
        }
      })

      if (checkout) {
        data.versions.unshift({
          current: typeof checkout !== 'undefined',
          selected: displayedVersion === 'checkout',
          timestamp: null,
          username: checkout.get('user').get('full_name'),
          versionId: null,
          versionNumber: null,
        })
      }

      return data
    },

    /**
     * Revert the content to a previous version.
     *
     * @param {number} versionId - Revert to the given version ID.
     *
     * @todo This manipulates a lot of stuff in the content block association,
     *   simply because the association already had important logic. The code
     *   should be moved here.
     *
     * @memberof AppBase.Entities.Content.Longform
     */
    restoreVersion: function (versionId) {
      var request
      var id = versionId || this.getMostRecentVersion().get('id')
      var blocks = this.get('content_blocks')
      blocks.locked = true

      request = jQuery.ajax({
        url: this.revertUrl(),
        type: 'POST',
        data: { version_id: id },
      })

      request.then(
        function (res) {
          blocks.cache.reset(res.content_blocks)
          blocks.reset(res.content_blocks)
          this.get('post').fetch()
          blocks.trigger('rerender')
          blocks.locked = false
          this.trigger('revert', id)
        }.bind(this)
      )
    },

    parse: function (response) {
      if (
        response &&
        response.content_blocks &&
        response.content_blocks.length > 0
      ) {
        var uuids = _.map(response.content_blocks, function (block) {
          return block.uuid
        })
        var deduped_blocks = []
        uuids.forEach(function (uuid) {
          deduped_blocks.push(
            _.find(response.content_blocks, function (block) {
              return block.uuid == uuid
            })
          )
        })
        response.content_blocks = deduped_blocks
      }
      return Backbone.Model.prototype.parse.apply(this, arguments)
    },

    /**
     * Tell the editor to display the specified version. If `versionId` is
     * `null` or `undefined`, the current version will be displayed. Updates
     * the `displayedVersion` attribute so views can update to reflect the
     * change.
     *
     * @param {(number|null)} versionId - The version model ID to display.
     *
     * @memberof AppBase.Entities.Content.Longform
     */
    showVersion: function (versionId) {
      var model
      var post = this.get('post')
      var versionNumber

      if (!versionId) {
        if (post.checkedOutByMe()) versionNumber = 'checkout'
        else versionNumber = this.getMostRecentVersion().get('version_number')
      } else {
        model = this.get('versions').get(versionId)
        versionNumber = model ? model.get('version_number') : null
      }

      this.set('displayedVersion', versionNumber)
      if (post) post.set('displayedVersion', versionNumber)
    },
  })
  Module.Longform = Model

  OpalRadio.Global.reqres.setHandler('longform:model', function (options) {
    var id =
      options.post instanceof Backbone.Model
        ? options.post.get('variant_id')
        : options.post.variant_id
    var existing = Backbone.Relational.store.find(Model, id)
    if (existing) return existing

    return new Model(options)
  })
})
